import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useRoom, useSlots } from '../api/queries'
import type { Slot } from '../api/types'
import { BookingDialog } from '../components/BookingDialog'
import { ErrorNotice } from '../components/ErrorNotice'
import { RoomImage } from '../components/RoomImage'
import { Badge, Button, Empty, Loading, PageTitle } from '../components/ui'
import { cn } from '../lib/cn'
import { people } from '../lib/words'
import {
  addDaysToKey,
  clockOffset,
  fmtDate,
  fmtDuration,
  fmtRange,
  fmtTime,
  isToday,
  minutesBetween,
  serverNow,
  todayKey,
  toIso,
} from '../lib/time'
import { useCanBook, useSession } from '../session'

/**
 * Экран 2.3 «Слоты комнаты» — второй сценарий заказчика: нужна именно эта комната,
 * потому что важна вместимость.
 *
 * Выделение диапазона: нажать на свободный слот, затем на конечный. Занятые слоты
 * не выделяются и диапазон через них не продлевается — иначе человек соберёт заявку,
 * которую сервер всё равно отклонит.
 *
 * Текущий получас доступен от текущей минуты (экран 2.15): в 13:45 бронь начинается
 * с 13:45, а не с 14:00.
 */
export function RoomSlotsPage() {
  const { roomId: roomIdParam } = useParams()
  const roomId = Number(roomIdParam)
  const [params, setParams] = useSearchParams()
  const date = params.get('date') ?? todayKey()

  const { policy } = useSession()
  const canBook = useCanBook()
  const navigate = useNavigate()

  const room = useRoom(Number.isFinite(roomId) ? roomId : undefined)
  const { data, isLoading, error, refetch } = useSlots(Number.isFinite(roomId) ? roomId : undefined, date)

  const [anchor, setAnchor] = useState<number | null>(null)
  const [head, setHead] = useState<number | null>(null)
  /**
   * Конец диапазона выбран нажатием, а не наведением.
   *
   * Без этого признака отличить «мышь проезжает по слотам» от «человек назвал конец»
   * нечем: наведение тоже пишет в head. Именно из-за этого второе нажатие — то самое,
   * которым по подсказке на экране диапазон и заканчивают, — попадало в ветку
   * «выделение уже готово, начинаем заново» и схлопывало диапазон в один получас.
   */
  const [fixed, setFixed] = useState(false)
  const [openBookingId, setOpenBookingId] = useState<number | null>(null)
  /** Почему последний клик не растянул диапазон. Пусто — растянул. */
  const [blocked, setBlocked] = useState<string | null>(null)
  /** Прошедшие получасы раскрыты вручную. Подробности — у firstUpcoming. */
  const [showPast, setShowPast] = useState(false)

  // Смена дня сбрасывает выделение: диапазон со вчерашнего дня бессмысленен.
  useEffect(() => {
    setAnchor(null)
    setHead(null)
    setFixed(false)
    setBlocked(null)
    setShowPast(false)
  }, [date, roomId])

  const slots = data?.slots ?? []

  /**
   * Экран 3.2 «Мобильный: слоты» — час выбран заранее.
   *
   * С мобильного списка комнат приходят за конкретным временем, и параметр at говорит,
   * за каким. Отмечаем этот слот сам, но только если он свободен и ещё не прошёл:
   * подставить занятое время — значит собрать заявку, которую сервер отклонит.
   */
  const at = params.get('at')
  const preselected = useRef(false)
  useEffect(() => {
    if (preselected.current || !at || slots.length === 0) return
    const index = slots.findIndex((slot) => fmtTime(slot.starts_at) === at)
    preselected.current = true
    if (index < 0) return
    const slot = slots[index]
    if (!slot?.free || new Date(slot.ends_at) <= new Date()) return
    setAnchor(index)
  }, [at, slots])

  const now = serverNow(clockOffset(data?.server_time))

  /**
   * Первый получас, который ещё можно занять. Всё до него — прошедшее время.
   *
   * Раньше прошедшие слоты оставались в сетке приглушёнными. Занять их нельзя,
   * а места они занимают столько же: открыв комнату в 18:00 при дне с 09:00,
   * человек с телефона пролистывает восемнадцать мёртвых клеток, прежде чем
   * дойдёт до первой доступной. Поэтому список начинается отсюда.
   *
   * Отсчёт по концу слота, а не по началу: текущий получас ещё не прошёл и
   * бронируется с текущей минуты (экран 2.15) — он должен остаться первым
   * в списке, это главный сценарий прихода с мобильного списка комнат.
   *
   * Совсем выбросить историю нельзя: на узком экране расписание дня показывает
   * по комнате только ближайшую встречу, и «что было в этой комнате утром»
   * больше нигде не увидеть. Поэтому прошедшее не удалено, а свёрнуто под
   * кнопку — по умолчанию его нет, по нажатию возвращается целиком.
   */
  const firstUpcoming = (() => {
    const index = slots.findIndex((slot) => new Date(slot.ends_at) > now)
    return index < 0 ? slots.length : index
  })()
  // Индексы в anchor/head — от начала полной сетки, поэтому режем не сами слоты,
  // а только то, что рисуем, и помним смещение.
  const shownFrom = showPast ? 0 : firstUpcoming
  const shown = slots.slice(shownFrom)

  /** Свободен ли отрезок целиком: через занятое диапазон не тянется. */
  const spanIsFree = (one: number, other: number) => {
    for (let index = Math.min(one, other); index <= Math.max(one, other); index += 1) {
      if (!slots[index]?.free) return false
    }
    return true
  }

  const range = useMemo(() => {
    if (anchor === null) return null
    const to = head ?? anchor
    const from = Math.min(anchor, to)
    const till = Math.max(anchor, to)
    // Через занятый слот диапазон не тянется.
    for (let index = from; index <= till; index += 1) {
      if (!slots[index]?.free) return null
    }
    const first = slots[from]
    const last = slots[till]
    if (!first || !last) return null
    // Текущий получас начинается с текущей минуты, а не с круглого времени.
    const startsAt =
      new Date(first.starts_at) < now && new Date(first.ends_at) > now ? toIso(now) : first.starts_at
    return { startsAt, endsAt: last.ends_at, from, till }
  }, [anchor, head, slots, now])

  const rangeMinutes = range ? minutesBetween(range.startsAt, range.endsAt) : 0
  const tooLong = rangeMinutes > policy.max_duration_minutes

  function setDate(next: string) {
    setParams({ date: next }, { replace: true })
  }

  /**
   * Нажатие по слоту.
   *
   * Отдельно про попытку растянуть диапазон через занятое время. Раньше такой клик
   * просто обнулял выделение: исчезала и подсветка, и нижняя панель с «Забронировать»,
   * причём молча. Со стороны это выглядит как несработавшая кнопка, и человек жмёт
   * ещё раз. Теперь прежнее выделение остаётся на месте, а рядом появляется строка
   * о том, что помешало: диапазон одним куском через чужую встречу не строится,
   * и это правило, а не сбой.
   */
  function pick(index: number) {
    const slot = slots[index]
    if (!slot) return
    if (!slot.free) {
      if (slot.booking_id) setOpenBookingId(slot.booking_id)
      return
    }
    if (anchor === null || fixed) {
      setAnchor(index)
      setHead(null)
      setFixed(false)
      setBlocked(null)
      return
    }
    if (!spanIsFree(anchor, index)) {
      const from = slots[Math.min(anchor, index)]
      const till = slots[Math.max(anchor, index)]
      setBlocked(
        `Между ${fmtTime(from!.starts_at)} и ${fmtTime(till!.ends_at)} комната уже занята — ` +
          'одним куском это время не забронировать. Выберите конец диапазона до занятого слота ' +
          'или начните выделение заново.',
      )
      return
    }
    setHead(index)
    setFixed(true)
    setBlocked(null)
  }

  return (
    <>
      <div className="mb-4">
        <Link to={`/?date=${date}`} className="text-sm text-muted hover:text-fg">
          ← Ко всем переговорным
        </Link>
      </div>

      <PageTitle>{room.data?.name ?? 'Переговорная'}</PageTitle>

      {room.data && (
        <div className="card mb-5 flex flex-col gap-4 p-4 sm:flex-row">
          {/*
            Пропорция картинки задана явно и совпадает с пропорцией рисунка (16:9).
            Пока карточка в одну колонку, высота считается от ширины — иначе на
            телефоне выходила широкая низкая полоса, из которой рисунок выступал
            вверх и вниз и терял стену с экраном. С sm карточка становится
            строкой, и там 256×144 — та же пропорция, но фиксированным блоком.
          */}
          <RoomImage
            name={room.data.name}
            capacity={room.data.capacity}
            photoUrl={room.data.photo_url}
            className="aspect-video w-full shrink-0 rounded-lg sm:h-36 sm:w-64"
          />
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              Вместимость: <span className="font-medium">{people(room.data.capacity)}</span>
            </p>
            {room.data.equipment.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {room.data.equipment.map((item) => (
                  <Badge key={item}>{item}</Badge>
                ))}
              </div>
            )}
            {!room.data.is_visible && (
              <p className="text-sm text-state-busy">
                Комната скрыта — сейчас она на ремонте и в поиске не показывается.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Предыдущий день"
          title="Предыдущий день"
          onClick={() => setDate(addDaysToKey(date, -1))}
        >
          <span aria-hidden>←</span>
        </Button>
        <span className="tnum min-w-[9rem] text-center text-sm font-medium">
          {isToday(date) ? 'Сегодня' : fmtDate(date)}
        </span>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Следующий день"
          title="Следующий день"
          onClick={() => setDate(addDaysToKey(date, 1))}
        >
          <span aria-hidden>→</span>
        </Button>
        <input
          type="date"
          aria-label="Дата"
          className="field tnum ml-1 h-8 w-auto py-0 text-sm"
          value={date}
          onChange={(event) => event.target.value && setDate(event.target.value)}
        />
      </div>

      {error && <ErrorNotice error={error} onRetry={() => void refetch()} className="mb-4" />}
      {isLoading && <Loading text="Считаем свободные слоты…" />}

      {/* Кнопка появляется только когда прошедшее и правда есть что показывать. */}
      {firstUpcoming > 0 && shown.length > 0 && (
        <div className="mb-2">
          <Button size="sm" variant="ghost" onClick={() => setShowPast(!showPast)}>
            {showPast ? 'Скрыть прошедшие' : `Показать прошедшие (${firstUpcoming})`}
          </Button>
        </div>
      )}

      {shown.length > 0 && (
        <div
          role="group"
          aria-label="Слоты по тридцать минут: нажмите начало, затем конец диапазона"
          className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6"
        >
          {shown.map((slot, offset) => {
            const index = shownFrom + offset
            return (
              <SlotButton
                key={slot.starts_at}
                slot={slot}
                now={now}
                selected={range !== null && index >= range.from && index <= range.till}
                pendingAnchor={anchor === index && !fixed}
                /* Наведение показывает будущий диапазон, но только если он вообще
                   строится: иначе панель с «Забронировать» пропадала под курсором,
                   стоило провести мышью через занятый слот. */
                onHover={() => {
                  if (anchor !== null && !fixed && spanIsFree(anchor, index)) setHead(index)
                }}
                onClick={() => pick(index)}
              />
            )
          })}
        </div>
      )}

      {/*
        Весь день позади: скрыть прошедшее здесь означало бы пустой экран, а пустой
        экран человек читает как поломку. Вместо нуля строк — объяснение и выход:
        куда идти за свободным временем и как всё-таки посмотреть, чем день был занят.
      */}
      {slots.length > 0 && shown.length === 0 && (
        <Empty
          title={isToday(date) ? 'На сегодня свободного времени больше нет' : 'Этот день уже прошёл'}
          hint={
            isToday(date)
              ? 'Рабочий день комнаты закончился — все получасы позади. Забронировать можно со следующего дня.'
              : 'Все получасы этого дня уже позади: забронировать задним числом нельзя.'
          }
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                variant="primary"
                onClick={() => setDate(isToday(date) ? addDaysToKey(date, 1) : todayKey())}
              >
                {isToday(date) ? 'Посмотреть завтра' : 'Вернуться к сегодня'}
              </Button>
              <Button onClick={() => setShowPast(true)}>Показать прошедшие ({slots.length})</Button>
            </div>
          }
        />
      )}

      {slots.length === 0 && !isLoading && !error && (
        <p className="hint">На этот день слотов нет — возможно, день нерабочий.</p>
      )}

      {range && (
        <div className="sticky bottom-4 mt-5">
          <div className="card flex flex-wrap items-center justify-between gap-3 p-4 shadow-pop">
            <div>
              <p className="tnum font-medium">
                {fmtRange(range.startsAt, range.endsAt)} · {fmtDuration(rangeMinutes)}
              </p>
              {/* Подсказка описывает то нажатие, которое сейчас доступно: пока конец
                  диапазона не назван — «растяните», после — «начнёте заново». Прежний
                  единственный текст врал ровно во втором случае. */}
              <p className="hint" role={blocked ? 'status' : undefined}>
                {blocked
                  ? blocked
                  : tooLong
                    ? `Дольше ${fmtDuration(policy.max_duration_minutes)} бронировать нельзя — сократите диапазон.`
                    : fixed
                      ? 'Диапазон выбран. Нажатие по другому слоту начнёт выделение заново.'
                      : 'Нажмите на другой свободный слот, чтобы растянуть диапазон.'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setAnchor(null)
                  setHead(null)
                  setFixed(false)
                  setBlocked(null)
                }}
              >
                Сбросить
              </Button>
              <Button
                variant="primary"
                disabled={!canBook || tooLong}
                onClick={() =>
                  navigate(
                    `/book?room=${roomId}&start=${encodeURIComponent(range.startsAt)}&end=${encodeURIComponent(range.endsAt)}`,
                  )
                }
              >
                Забронировать
              </Button>
            </div>
          </div>
        </div>
      )}

      {openBookingId && (
        <BookingDialog bookingId={openBookingId} onClose={() => setOpenBookingId(null)} />
      )}
    </>
  )
}

function SlotButton({
  slot,
  now,
  selected,
  pendingAnchor,
  onClick,
  onHover,
}: {
  slot: Slot
  now: Date
  selected: boolean
  pendingAnchor: boolean
  onClick: () => void
  onHover: () => void
}) {
  const past = new Date(slot.ends_at) <= now
  const startsNow = new Date(slot.starts_at) < now && new Date(slot.ends_at) > now

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onHover}
      disabled={past}
      aria-pressed={selected || pendingAnchor}
      title={slot.free ? undefined : slot.title || 'Занято'}
      className={cn(
        'flex flex-col items-start rounded-lg border px-3 py-2 text-left transition',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        past && 'cursor-not-allowed opacity-40',
        !slot.free && 'border-line bg-surface-3',
        slot.free && !selected && 'border-line bg-surface hover:border-accent',
        (selected || pendingAnchor) && 'border-accent bg-accent-soft',
      )}
    >
      <span className="tnum w-full truncate text-sm font-medium">
        {startsNow ? 'сейчас' : fmtTime(slot.starts_at)} – {fmtTime(slot.ends_at)}
      </span>
      {/* w-full обязательно: без него truncate не работает — колонка сетки растягивается
          по содержимому, и длинная тема встречи вылезает на соседний слот. */}
      <span className="w-full truncate text-xs text-muted">
        {slot.free ? 'Свободно' : slot.title || 'Занято'}
      </span>
    </button>
  )
}
